import CheckIcon from "@mui/icons-material/Check";
import { Box, Stack, TextField, Theme, Typography, useTheme } from "@mui/material";
import type { JSX } from "react";
import { useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  SprengnetterValuationHubValuationReportModelResponseAvmPriceResponse as AvmPriceResponse,
  SprengnetterValuationHubProsaWebModelResponseProsaWebResponse as ProsaWebResponse,
  ValuationHubCoreModelDtoValuationReportConfigurationDto,
} from "@/api/generatedApi";
import { ValuationPriceTypeBox } from "@/components/ValuationPriceTypeBox/ValuationPriceTypeBox";
import { getAvmDisplayedRange } from "@/helpers/avm";
import { getProsaWebMarketValue } from "@/helpers/prosaWeb";
import { useModuleContext } from "@/hooks/useModuleContext";
import { useAppSelector } from "@/store/hooks";
import { colors } from "@/theme/tokens/colors";
import { ObjectTypeEnum, PropertyInformation } from "@/types/refineRealEstate";

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const createStyles = (theme: Theme) => ({
  title: {
    fontWeight: 700,
    fontSize: "18px",
    lineHeight: "150%",
  },
  containerGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    columnGap: theme.analysisLayout.panelGap,
    rowGap: theme.analysisLayout.compactGap,
    width: "100%",
  },
  contentListContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "15px",
    backgroundColor: colors.brand.blueLight,
    borderRadius: "5px",
    padding: "13px",
    width: "100%",
  },
  contentList: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    backgroundColor: colors.brand.blueLight,
  },
  contentListIcon: {
    height: theme.analysisLayout.titleFontSize,
  },
  reportScope: {
    fontSize: "16px",
    lineHeight: "28px",
    fontWeight: 400,
    color: "#333333",
  },
  askingPriceInput: {
    width: "100%",
    minWidth: 0,
    "& .MuiOutlinedInput-root": {
      height: "40px",
      backgroundColor: "white",
    },
    "& input": {
      textAlign: "right",
      fontSize: "16px",
      fontWeight: 600,
      padding: "8px 12px",
    },
  },
});

interface PriceAndValuationMethodsSectionProps {
  propertyInformation: PropertyInformation | null;
  reportAskingPrice?: number | null;
  onAskingPriceChange?: (price: number | null) => void;
  isAskingPriceEditable?: boolean;
  avmPriceResult: AvmPriceResponse | null;
  prosaWebPriceResult: ProsaWebResponse | null;
}

type ContentEntry = { index: number; label: string };

export const PriceAndValuationMethodsSection = ({
  propertyInformation,
  reportAskingPrice,
  onAskingPriceChange,
  isAskingPriceEditable = false,
  avmPriceResult,
  prosaWebPriceResult,
}: PriceAndValuationMethodsSectionProps): JSX.Element => {
  const theme = useTheme();
  const styles = createStyles(theme);
  const { t } = useTranslation("ValuationReportGenerationPanel");
  const { isValuationReport } = useModuleContext();
  const reportSelectedEquipmentPriceRange = useAppSelector(
    (state) => state.valuationReport.selectedEquipmentPriceRange,
  );
  const { t: tMarketAnalysis } = useTranslation("MarketAnalysisPanel");
  const { watch, setValue } = useFormContext<ValuationHubCoreModelDtoValuationReportConfigurationDto>();
  const valuationType = watch("valuation_type");
  const askingPrice =
    reportAskingPrice !== undefined ? reportAskingPrice : propertyInformation?.financialAndRentalInfo.price?.value;
  const [localAskingPrice, setLocalAskingPrice] = useState("");
  const [previewAskingPrice, setPreviewAskingPrice] = useState<number | null>(askingPrice ?? null);
  const [isAskingPriceFocused, setIsAskingPriceFocused] = useState(false);
  const askingPriceInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setLocalAskingPrice(askingPrice != null ? askingPrice.toLocaleString("de-DE") : "");
    setPreviewAskingPrice(askingPrice ?? null);
  }, [askingPrice]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      const numericValue = localAskingPrice.replace(/\D/g, "");
      setPreviewAskingPrice(numericValue ? parseInt(numericValue, 10) : null);
    }, 200);

    return (): void => window.clearTimeout(timeoutId);
  }, [localAskingPrice]);
  const isPlot =
    propertyInformation?.generalInfo.objectType === ObjectTypeEnum.plotOfLand ||
    (propertyInformation?.buildingData.livingArea == null && propertyInformation?.plotData.plotArea != null);
  const rawCalculationArea = isPlot
    ? propertyInformation?.plotData.plotArea
    : propertyInformation?.buildingData.livingArea;
  const numericCalculationArea = rawCalculationArea == null ? undefined : Number(rawCalculationArea);
  const calculationArea =
    numericCalculationArea != null && Number.isFinite(numericCalculationArea) && numericCalculationArea > 0
      ? numericCalculationArea
      : undefined;
  const askingPricePerSquareMeter =
    previewAskingPrice != null && calculationArea != null
      ? Math.round(previewAskingPrice / calculationArea)
      : undefined;
  const prosaWebPrice = getProsaWebMarketValue(prosaWebPriceResult, {
    useRoundedValue: true,
    objectType: propertyInformation?.generalInfo.objectType,
    livingArea: propertyInformation?.buildingData.livingArea,
  });
  const avmDisplayedRange = getAvmDisplayedRange(
    avmPriceResult,
    isValuationReport ? reportSelectedEquipmentPriceRange : undefined,
  );
  const prosaWebPricePerSquareMeter =
    prosaWebPrice != null && calculationArea != null ? Math.round(prosaWebPrice / calculationArea) : undefined;
  const avmPriceMin = avmDisplayedRange.min;
  const avmPriceMax = avmDisplayedRange.max;
  const avmPricePerSquareMeterMin =
    avmPriceMin != null && calculationArea != null ? Math.round(avmPriceMin / calculationArea) : undefined;
  const avmPricePerSquareMeterMax =
    avmPriceMax != null && calculationArea != null ? Math.round(avmPriceMax / calculationArea) : undefined;

  const isAvmValuationSelected = valuationType === "AVM" || valuationType === "ALL";
  const isProsaWebValuationSelected = valuationType === "PROSAWEB" || valuationType === "ALL";

  const handleAvmChange = (newValue: boolean): void => {
    if (!newValue && !isProsaWebValuationSelected) {
      return;
    }

    setValue("valuation_type", newValue && isProsaWebValuationSelected ? "ALL" : newValue ? "AVM" : "PROSAWEB");
  };

  const handleMarketValueChange = (newValue: boolean): void => {
    if (!newValue && !isAvmValuationSelected) {
      return;
    }

    setValue("valuation_type", newValue && isAvmValuationSelected ? "ALL" : newValue ? "PROSAWEB" : "AVM");
  };

  const commonContentEntries = [
    { index: 1, label: t("reportPanel.configuration.contentSummary") },
    { index: 2, label: t("reportPanel.configuration.contentPropertyDescription") },
    { index: 7, label: t("reportPanel.configuration.contentPerformance") },
    { index: 8, label: t("reportPanel.configuration.contentPhotos") },
  ];

  const avmContentEntries = [
    { index: 3, label: t("reportPanel.configuration.contentMicroLocationAnalysis") },
    { index: 4, label: t("reportPanel.configuration.contentMarketAnalysis") },
    { index: 6, label: t("reportPanel.configuration.contentComparables") },
  ];

  const marketvalueContentEntries = [
    { index: 3, label: t("reportPanel.configuration.contentAdvancedMicroLocationAnalysis") },
    { index: 5, label: t("reportPanel.configuration.contentMarketValueAssesment") },
  ];

  const getContentEntries = (): ContentEntry[] => {
    const merged: Record<number, ContentEntry> = {};

    for (const entry of commonContentEntries) {
      merged[entry.index] = entry;
    }

    if (isAvmValuationSelected) {
      for (const entry of avmContentEntries) {
        merged[entry.index] = entry;
      }
    }

    if (isProsaWebValuationSelected) {
      for (const entry of marketvalueContentEntries) {
        merged[entry.index] = entry;
      }
    }

    return Object.values(merged).sort((a, b) => a.index - b.index);
  };

  const saveAskingPrice = (): void => {
    if (!onAskingPriceChange || !isAskingPriceEditable) return;

    const numericValue = localAskingPrice.replace(/\D/g, "");
    onAskingPriceChange(numericValue ? parseInt(numericValue, 10) : null);
  };

  const handleAskingPriceKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key !== "Enter") return;

    event.preventDefault();
    event.stopPropagation();
    askingPriceInputRef.current?.blur();
  };

  const askingPriceControl =
    isAskingPriceEditable && onAskingPriceChange ? (
      <TextField
        inputRef={askingPriceInputRef}
        value={isAskingPriceFocused ? localAskingPrice : localAskingPrice ? localAskingPrice + " €" : ""}
        placeholder={tMarketAnalysis("askingPriceOnRequest")}
        onChange={(event): void => {
          const numericValue = event.target.value.replace(/\D/g, "");
          setLocalAskingPrice(numericValue ? parseInt(numericValue, 10).toLocaleString("de-DE") : "");
        }}
        onFocus={(): void => setIsAskingPriceFocused(true)}
        onBlur={(): void => {
          setIsAskingPriceFocused(false);
          saveAskingPrice();
        }}
        onKeyDown={handleAskingPriceKeyDown}
        inputProps={{
          inputMode: "numeric",
          "aria-label": t("reportPanel.configuration.askingPrice"),
          "data-report-asking-price-input": true,
        }}
        sx={styles.askingPriceInput}
        data-cy="report-configuration-asking-price-input"
      />
    ) : askingPrice ? (
      askingPrice.toLocaleString("de-DE") + " €"
    ) : (
      tMarketAnalysis("askingPriceOnRequest")
    );

  const reportContentDescription =
    isAvmValuationSelected && isProsaWebValuationSelected
      ? t("reportPanel.configuration.reportScopeAll")
      : isAvmValuationSelected
        ? t("reportPanel.configuration.reportScopeAvm")
        : isProsaWebValuationSelected
          ? t("reportPanel.configuration.reportScopeSone")
          : "";

  return (
    <Box sx={styles.containerGrid}>
      <Stack alignItems={"start"} direction="column" spacing="8px">
        <Typography variant={"inputLabel"}>{t("reportPanel.configuration.valuationMethod")}</Typography>
        <Typography>{t("reportPanel.configuration.valuationMethodDescription")}</Typography>
      </Stack>
      <Stack alignItems={"start"} direction="column" spacing="8px">
        <Typography variant={"inputLabel"}>{t("reportPanel.configuration.contentListTitle")}</Typography>
        <Typography>{t("reportPanel.configuration.contentListDescription")}</Typography>
      </Stack>
      <Stack alignItems={"start"} direction="column" spacing="14px">
        <ValuationPriceTypeBox
          title={t("reportPanel.configuration.askingPrice")}
          subTitle={t("reportPanel.configuration.askingPriceDescription")}
          priceMain={askingPriceControl}
          priceSub={askingPricePerSquareMeter ? `${askingPricePerSquareMeter?.toLocaleString("de-DE")} €/m²` : null}
          selectable={true}
          isSelected={true}
          disabled
          compactPriceLayout
        />
        <ValuationPriceTypeBox
          title={t("reportPanel.configuration.avmPrice")}
          subTitle={t("reportPanel.configuration.avmPriceDescription")}
          priceMain={
            avmPriceMin && avmPriceMax
              ? `${avmPriceMin?.toLocaleString("de-DE")} - ${avmPriceMax?.toLocaleString("de-DE")} €`
              : "—"
          }
          priceSub={
            avmPricePerSquareMeterMin && avmPricePerSquareMeterMax
              ? `${avmPricePerSquareMeterMin?.toLocaleString("de-DE")} - ${avmPricePerSquareMeterMax?.toLocaleString("de-DE")} €/m²`
              : "—"
          }
          selectable={true}
          isSelected={isAvmValuationSelected}
          setIsSelected={handleAvmChange}
          disabled={!(avmPriceMin && avmPriceMax)}
        />
        <ValuationPriceTypeBox
          title={t("reportPanel.configuration.sonePrice")}
          subTitle={t("reportPanel.configuration.sonePriceDescription")}
          priceMain={prosaWebPrice ? `${prosaWebPrice?.toLocaleString("de-DE")} €` : "—"}
          priceSub={prosaWebPricePerSquareMeter ? `${prosaWebPricePerSquareMeter?.toLocaleString("de-DE")} €/m²` : "—"}
          selectable={true}
          isSelected={isProsaWebValuationSelected}
          setIsSelected={handleMarketValueChange}
          disabled={!prosaWebPrice}
        />
      </Stack>
      <Stack alignItems={"start"} direction="column" spacing="10px">
        <Box sx={styles.contentListContainer}>
          <Box sx={styles.contentList}>
            {getContentEntries().map((contentEntry) => (
              <Stack alignItems={"center"} direction="row" spacing="15px" key={contentEntry.index}>
                <CheckIcon sx={styles.contentListIcon} />
                <Typography variant={"body1"}>{contentEntry.label}</Typography>
              </Stack>
            ))}
          </Box>
          <Typography>
            <span style={{ fontWeight: 600 }}>{t("reportPanel.configuration.reportScope")} </span>
            <span>{reportContentDescription}</span>
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
};
